import React, { FC, useCallback, useMemo } from 'react'

import { alpha, Box, Palette, useTheme } from '@mui/material'
import { isEmpty } from 'lodash'
import { Bar, Line } from 'react-chartjs-2'

import { chartOptions } from '../chartOptions'
import { EmptyData } from '../EmptyData'
import { ReportWidgetsHeader } from '../ReportWidgetsHeader'
import { StyledWidgetWrapper } from '../StyledWidgetWrapper'
import { DrawdownChartProps, DrawdownChartTypeEnum } from '../types'

export const getDrawdownChartSettings = (palette: Palette, chartType: DrawdownChartTypeEnum) => {
  switch (chartType) {
    case DrawdownChartTypeEnum.bar:
      return {
        backgroundColor: palette.blue[500],
        fill: true,
        borderWidth: 2,
        tension: 0.5,
        minBarLength: 3,
      }
    case DrawdownChartTypeEnum.linear:
      return {
        backgroundColor: alpha(palette.red[400] as string, 0.5),
        fill: true,
        borderColor: palette.red[400],
        borderWidth: 2,
        pointRadius: 2,
        tension: 0.5,
      }
    default:
      return {}
  }
}

export const CommonDrawdownChart: FC<DrawdownChartProps> = ({
  displayWidth,
  data,
  chartLabel,
  chartHeaderText,
  chartHeaderExplanation,
  chartType,
  currency,
}) => {
  const { spacing, palette } = useTheme()

  const chartStyles = getDrawdownChartSettings(palette, chartType)

  const chartData = {
    labels: data?.map((item) => item.period),
    datasets: [
      {
        label: chartLabel,
        data: data?.map((item) => item.value),
        ...chartStyles,
      },
    ],
  }

  const getChartType = useCallback(() => {
    switch (chartType) {
      case DrawdownChartTypeEnum.bar:
        return Bar
      case DrawdownChartTypeEnum.linear:
        return Line
    }
  }, [])

  const Chart = getChartType()
  const options = useMemo(() => chartOptions({ currency }), [currency])

  return (
    <StyledWidgetWrapper>
      <ReportWidgetsHeader text={chartHeaderText} explanation={chartHeaderExplanation} />

      {isEmpty(data) ? (
        <Box mt={4}>
          <EmptyData />
        </Box>
      ) : (
        <Box>
          <Chart
            data={chartData}
            style={{ maxHeight: spacing(31) }}
            key={displayWidth}
            options={options}
          />
        </Box>
      )}
    </StyledWidgetWrapper>
  )
}
